import "./Select.css"
import React, {
  ForwardedRef,
  ReactElement,
  useId,
  FunctionComponent,
  SVGProps,
} from "react";
import classNames from "classnames";

import { ReactComponent as ArrowIcon } from "@icons/arrow.svg";
import { ReactComponent as ErrorIcon } from "@icons/error.svg";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  helperText?: string;
  state?: "error";
};

export const Select = React.forwardRef(
  (
    { label, helperText, state, children, value, ...props }: SelectProps,
    ref?: ForwardedRef<HTMLSelectElement>
  ): ReactElement => {
    const uniqueId = useId();

    let stateClasses = "";
    let MessageIcon: FunctionComponent<SVGProps<SVGSVGElement>> | null = null;

    switch (state) {
      case "error":
        stateClasses = "outline outline-2 outline-brand-red";
        MessageIcon = ErrorIcon;
        break;
    }

    return (
      <div className="relative space-y-1">
        {label && (
          <label
            htmlFor={uniqueId}
            className="font-bold text-navy-blue text-base"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            aria-describedby={`aria-${uniqueId}`}
            id={uniqueId}
            value={value ?? ""}
            {...props}
            className={classNames(
              "wisdom-select text-ellipsis appearance-none w-full h-[50px] bg-l-blue font-bold rounded-[6px] pl-4 pr-12 inset-shadow-md", value ? "text-navy-blue" : "text-navy-blue-bright", "focus:outline-brand-purple", stateClasses, props.className)}
          >
            {props?.placeholder && (<option value="" disabled> {props.placeholder} </option>)}
            {children}
          </select>

          <div className="absolute top-1/2 -translate-y-1/2 right-4 pointer-events-none">
            <ArrowIcon width={20} height={20} />
          </div>
        </div>

        {helperText && (
          <div className="flex items-center mt-2">
            {MessageIcon && <MessageIcon width={16} height={18} />}
            <p id={`aria-${uniqueId}`} className="pl-1 text-sm">{helperText}</p>
          </div>
        )}
      </div>
    );
  }
);
